/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-destructuring */
import type { TransformedHotelOffer } from '@lt/api/dist/methods/search/types';
import { maxBy, minBy } from 'lodash';

const SEPARATOR = '|';

const WiFiTypes = {
  ALL: 'Все отели',
  FREE: 'Бесплатный Wi-Fi',
  PAID: 'Есть Wi-Fi',
};

const parseSelectedWifis = (wifis) => {
  let name = '';
  if (!wifis.length) {
    name = WiFiTypes.ALL;
  } else if (wifis.some((w) => w.id === 'PAID')) {
    name = WiFiTypes.PAID;
  } else {
    name = WiFiTypes.FREE;
  }

  return name;
};

// Фильтруем по условию, если ни один элемент не попадает под условие - берем все
const filterOrTakeAll = <T>(
  array: T[],
  comparator: (value: T, index: number, array: T[]) => boolean,
) => {
  const value = array.filter(comparator);

  return value.length ? value : array;
};

type SearchParams = {
  to: any;
  from: any;
  kids: { ages: number[] };
  nights: { from: number; to: number };
  options: { flex_dates: boolean };
  tour_ids?: string[];
  surcharges_by_level?: any[];
  hotel_ids?: string[];
  accommodation_availability?: string[];
  flight_seats?: string[];
  bundle_ids?: string[];
  acm_component_ids?: string[];
  adults?: number;
  start_date?: string;
};

type FilterParams = { [key: string]: any };

type Params = {
  filterParams?: FilterParams;
  requestId?: string;
  result?: TransformedHotelOffer[];
  hotelsCount?: number;
  searchParams?: SearchParams;
  searchType?: string;
  sortBy?: string;
  sortOrder?: string;
  originalSortBy?: string;
  status?: string[];
  localization?: {
    currencyCode: string;
  };
};

type ParsedFields = {
  [key: string]: any;
};

const parseSearchFields = (
  searchParams: SearchParams,
  requestId?: string,
  searchType?: string,
  currencyCode?: string,
): ParsedFields => {
  const destination = searchParams.to;
  const dCity = destination?.city || null;
  const dMetaregion = destination?.metaregion || null;
  const dPlace = dCity || dMetaregion;
  const dCountry = destination?.country || null;
  const departure = searchParams.from;
  const city = departure?.city || null;
  const kids = searchParams.kids || { ages: [] };
  const kidsAges = kids.ages || [];
  const nights = searchParams.nights || { from: 0, to: 0 };
  const options = searchParams.options || { flex_dates: false };

  return {
    tour_ids: searchParams.tour_ids?.join(SEPARATOR) || null,
    surcharges_by_level:
      searchParams.surcharges_by_level
        ?.map((s) => JSON.stringify(s))
        .join(SEPARATOR) || null,
    adults_count: searchParams.adults || 0,
    country_id: dCountry?.id || null,
    country_name: dCountry?.name_en || '',
    departure_id: city?.id || null,
    departure_name: city?.name_en || '',
    hotel_ids: searchParams.hotel_ids?.join(SEPARATOR) || null,
    accommodation_availability:
      searchParams.accommodation_availability?.join(SEPARATOR) || null,
    flight_seats: searchParams.flight_seats?.join(SEPARATOR) || null,
    is_flex_start_date: !!options.flex_dates,
    kids_ages: kidsAges.join(SEPARATOR),
    kids_count: kidsAges.length,
    nights_from: nights.from,
    nights_to: nights.to,
    request_id: requestId || null,
    start_timestamp: searchParams.start_date
      ? Math.floor(new Date(searchParams.start_date).getTime() / 1000)
      : null,
    to_place_id: dPlace?.id || 0,
    to_place_name: dPlace?.name_en || '',
    search_type: searchType || '',
    currency_code: currencyCode || '',
    bundle_ids: searchParams.bundle_ids?.join(',') || null,
    acm_component_ids: searchParams.acm_component_ids?.join(',') || null,
  };
};

const parseFilterFields = (filterParams: FilterParams): ParsedFields => {
  const beachTypes = filterParams.beach_type;
  const beaches = filterParams.beach_surface;
  const confirms = filterParams.instant_confirm;
  const hotelQuery = filterParams.hotel_name;
  const hotelTypes = filterParams.category;
  const designatedBeach = beachTypes?.find((type) => type.id === 'DESIGNATED');
  const meals = filterParams.meals;
  const operators = filterParams.operators;
  const prices = filterParams.prices;
  const paymentBenefits = filterParams.payment_benefits;
  const travelBenefits = filterParams.travel_benefits;
  const rating = filterParams.rating;
  const regions = filterParams.regions;
  const seaLines = filterParams.line;
  const selectedBeaches = beaches
    ? filterOrTakeAll(beaches, (b: any) => b.selected)
    : [];
  const selectedConfirm = confirms?.find((c) => c.selected);
  const selectedHotelTypes = hotelTypes
    ? filterOrTakeAll(hotelTypes, (b: any) => b.selected)
    : [];
  const selectedMeals = meals
    ? filterOrTakeAll(meals, (m: any) => m.selected)
    : [];
  const selectedOperators = operators
    ? filterOrTakeAll(operators, (o: any) => o.selected)
    : [];
  const selectedRating = rating?.find((r) => r.selected);
  const selectedRegions = regions
    ? filterOrTakeAll(regions, (r: any) => r.selected)
    : [];
  const skiDistance = filterParams.ski_distance;
  const stars = filterParams.stars;
  const wifis = filterParams.wifi;
  const selectedWifis = wifis
    ? filterOrTakeAll(wifis, (w: any) => w.selected)
    : [];

  return {
    beach_designated: designatedBeach?.selected || false,
    beach_ids: selectedBeaches.map((b) => b.id).join(SEPARATOR),
    beach_names: selectedBeaches.map((b) => b.name).join(SEPARATOR),
    budget_from: prices?.minPrice || prices?.analytics_min || 0,
    budget_to: prices?.maxPrice || prices?.analytics_max || null,
    hotel_types: selectedHotelTypes.map((t) => t.id).join(SEPARATOR),
    hotel_types_ids: selectedHotelTypes.map((t) => t.id).join(SEPARATOR),
    hotel_query: hotelQuery?.value || '',
    instant_confirm: selectedConfirm?.id || false,
    filter_travel_benefits: travelBenefits || null,
    filter_payments: paymentBenefits || null,
    meals_ids: selectedMeals.map((m: any) => m.id).join(SEPARATOR),
    meals_names: selectedMeals.map((m: any) => m.name).join(SEPARATOR),
    operators_ids: selectedOperators.map((o: any) => o.id).join(SEPARATOR),
    operators_names: selectedOperators.map((o) => o.name).join(SEPARATOR),
    rating_from: selectedRating?.id || 0,
    rating_to: 10,
    region_ids: selectedRegions.map((r) => r.id).join(SEPARATOR),
    region_names: selectedRegions.map((r) => r.name).join(SEPARATOR),
    sea_lines: seaLines?.map((l) => l.id).join(SEPARATOR),
    ski_distance: skiDistance?.map((d) => d.id).join(SEPARATOR),
    stars: stars
      ?.map((s) => s.id)
      .sort()
      .join(SEPARATOR),
    wifi_ids: selectedWifis.map((w) => w.id).join(SEPARATOR),
    wifi_names: selectedWifis ? parseSelectedWifis(selectedWifis) : '',
  };
};

const parseResultFields = (
  result: TransformedHotelOffer[],
  hotelsCount?: number,
): ParsedFields => {
  const maxPriceHotel = maxBy(result, 'analyticsInfo.maxPrice');
  const minPriceHotel = minBy(result, 'analyticsInfo.minPrice');

  return {
    result_count: hotelsCount || 0,
    result_maxprice: maxPriceHotel?.analyticsInfo.maxPrice || null,
    result_minprice: minPriceHotel?.analyticsInfo.minPrice || null,
    result_minprices: result
      .map((item) => item.analyticsInfo.minPrice)
      .join(SEPARATOR),
    result_surcharges: result
      .map((item) => item.analyticsInfo.surcharge)
      .join(SEPARATOR),
    instant_confirm_labels: result
      .map((hotel) => hotel.instantConfirm)
      .join(SEPARATOR),
    labels: result
      .map((hotel) => hotel.labels.map((label) => label.id).join(';'))
      .join(SEPARATOR),
    confirmability_index: result
      .map((hotel) => hotel.analyticsInfo.confirmabilityIndex)
      .join(SEPARATOR),
  };
};

export const parseTourSearchParams = (params: Params): ParsedFields => {
  const searchFields = params.searchParams
    ? parseSearchFields(
        params.searchParams,
        params.requestId,
        params.searchType,
        params.localization?.currencyCode,
      )
    : {};

  const filterFields = params.filterParams
    ? parseFilterFields(params.filterParams)
    : {};

  const resultFields = params.result
    ? parseResultFields(params.result, params.hotelsCount)
    : {};

  return {
    ...searchFields,
    ...filterFields,
    ...resultFields,
    sorting: params.sortBy || '',
    sorting_order: params.sortOrder || '',
    originalSortBy: params.originalSortBy || '',
    result_operators: params.status?.join(SEPARATOR) || '',
    result_statuses: params.status?.join(SEPARATOR) || '',
  };
};
